import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { I18nProvider } from './i18n';
import { RecoSessionProvider } from './context/RecoSessionProvider';

import MarketingPage from './pages/MarketingPage';
import SignupFlow from './components/auth/SignupFlow';
import LoginForm from './components/auth/LoginForm';
import RequestWaitlistForm from './components/RequestWaitlistForm';
import TheCollectiv3 from './pages/TheCollectiv3';
import TheCollectiv3_Admin from './pages/TheCollectiv3 admin';  
import TheCollectiv3_PLY from './pages/TheCollective3 ply';
import MarketingGallery from './pages/MarketingGallery';
import TheCollectiv3_PLYbackup from './pages/TheCollective3 plybackup';
import IframeTestPage from './pages/IframeTestPage';
import GeoAutomationViewer from './pages/GeoAutomationViewer';
import GeoAutomationViewerWithPOI from './pages/GeoAutomationWithPOIViewer';
import MarketingTool from './pages/MarketingTool';  
import NotFoundPage from './pages/NotfoundPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import GDPRAddendumPage from './pages/GDPRAddendumPage';
import UpdateProfile from './components/auth/UpdateProfileForm';
import { useAuth } from './contexts/AuthContext';
import UnauthorizedPage from './pages/UnauthorizedPage';
import Layout from './components/layout';
import { logout as authLogout } from './services/auth';
import AdminDashboard from './pages/AdminDashboard';
import FeedbackForm from './components/FeedbackForm';
import ProtectedRoute from './components/auth/ProtectedRoute';
import CustomerSupport from './components/CustomerSupportForm';
import Gallery from './pages/Gallery';
import { useUploadCtx } from './contexts/UploadContext';
import RoleBasedRoute from './components/RoleBasedRoute';
import SceneManager from './components/SceneManager';
import IndoorCaptureGuidePage from './pages/IndoorCaptureGuidePage';
import DroneCaptureGuidePage from './pages/DroneCaptureGuidePage';
import { ConsentBanner } from './components/ConsentBanner';
import { useAnalytics } from './hooks/useAnalytics';
import SceneViewerPage from './pages/SceneViewerPage';
import MultiSceneViewerPage from './pages/MultiSceneViewerPage';
import ChatWithRecoPage from './pages/ChatWithRecoPage';
import Cr3ateDesktopPage from './pages/Cr3ateDesktopPage';
import Cr3ateSpatialModelsPage from './pages/Cr3ateSpatialModelsPage';
import Cr3ateTechnologyPage from './pages/Cr3ateTechnologyPage';
import Cr3ateSdkPage from './pages/Cr3ateSdkPage';
import ShowcasePage from './pages/ShowcasePage';
import RealEstateDemo from './pages/RealEstateDemo';
import MedicalLakeRedfinDemo from './pages/MedicalLakeRedfinDemo';
import MedicalLakeRealtorDemo from './pages/MedicalLakeRealtorDemo';
import StadiumDronePlannerPage from './pages/StadiumDronePlannerPage';
import EmbedViewerPage from './pages/EmbedViewerPage';
import RealEstateViewerPage from './pages/RealEstateViewerPage';
import SplatterboxInsightsPage from './pages/SplatterboxInsightsPage';
import MarketingAgentPage from './pages/MarketingAgentPage';
import TeamEmailPage from './pages/TeamEmailPage';

function AnalyticsWrapper() {
  useAnalytics('G-83EQH0PZF5');
  return null;
}

function App() {
  const { user: authUser } = useAuth();
  const { reset } = useUploadCtx();
  // These internal viewer entry points remain available for development and
  // staging validation, but are intentionally not exposed by the production
  // web app yet.
  const exposeInternalViewers = import.meta.env.MODE !== 'production';
  const enableLocalTeamEmail =
    import.meta.env.MODE === 'localdev' && import.meta.env.VITE_ENABLE_TEAM_EMAIL === 'true';


  const handleLogout = async () => {
    try {
      console.log('Starting logout process...');
      
      // Clear consent - gives users a fresh choice next time
      localStorage.removeItem('analytics_consent');

      const success = await authLogout();

      if (success) {
        console.log('Logout successful');
        reset();
        window.location.href = '/';
      } else {
        console.warn('Logout completed with warnings');
        reset();
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Logout error:', error);
      reset();
      window.location.href = '/';
    }
  };

  // Create user object for legacy components that expect it
  const legacyUser = authUser
    ? {
        id: authUser.uid,
        name: authUser.displayName || authUser.email?.split('@')[0] || 'User',
        email: authUser.email || 'No email provided',
        createdAt: Date.now(),
      }
    : null;

  return (
    <Router>
      {/* Add analytics and consent banner - non-blocking, sticky until response */}
      <AnalyticsWrapper />
      <ConsentBanner />
      {/* Reco has two presentations — the floating widget on the landing page and
          the full page at /reco — and they are separate routes. Holding the
          session above <Routes> is what lets a user move between them without
          losing the conversation. I18n sits above it in turn, so the locale that
          seeded the transcript is the same one the panel renders in. */}
      <I18nProvider>
      <RecoSessionProvider>
      <Routes>
        {/* Public routes WITHOUT layout - Marketing page as default */}
        <Route path="/" element={<MarketingPage />} />
        <Route path="/login" element={<LoginForm />} />
        <Route path="/signup" element={<SignupFlow />} />
        <Route path="/request-waitlist" element={<RequestWaitlistForm />} /> 
        <Route path="/the-galleryAdmin" element={<TheCollectiv3_Admin />} />  
        <Route path="/iframe-test" element={<IframeTestPage />} />
        <Route path="/GeoAutomationViewer" element={<GeoAutomationViewer />} />
        <Route path="/GeoAutomationWithPOIViewer" element={<GeoAutomationViewerWithPOI />} />
        <Route path="/marketing-gallery" element={<MarketingGallery />} />
        <Route path="/showcase" element={<ShowcasePage />} />
        <Route path="/realEstateDemo" element={<RealEstateDemo />} />
        <Route path="/redfin-demo" element={<MedicalLakeRedfinDemo />} />
        <Route path="/realtor-demo" element={<MedicalLakeRealtorDemo />} />
        <Route path="/stadium-drone-planner" element={<StadiumDronePlannerPage />} />
        <Route path="/viewer-embed" element={<EmbedViewerPage />} />
        <Route path="/realestate-viewer" element={<RealEstateViewerPage />} />
        {exposeInternalViewers && (
          <>
            <Route path="/viewer" element={<SceneViewerPage />} />
            <Route path="/multi-viewer" element={<MultiSceneViewerPage />} />
          </>
        )}
        <Route path="/reco" element={<ChatWithRecoPage />} />
        <Route path="/desktop" element={<Cr3ateDesktopPage />} />
        <Route path="/spatial-models" element={<Cr3ateSpatialModelsPage />} />
        <Route path="/technology" element={<Cr3ateTechnologyPage />} />
        <Route path="/sdk" element={<Cr3ateSdkPage />} />
        <Route path="/the-galleryPly" element={<TheCollectiv3_PLY />} />  
        <Route path="/marketingTool" element={<MarketingTool />} />  
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/privacy/gdpr" element={<GDPRAddendumPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="/not-found" element={<NotFoundPage />} />
        {import.meta.env.VITE_ENABLE_SPLATTERBOX === 'true' && (
          <>
            <Route path="/splatterbox" element={<SplatterboxInsightsPage />} />
            <Route path="/marketing-agent" element={<MarketingAgentPage />} />
          </>
        )}

        {/* Gallery routing - for both non logged in and logged in user */}
        {authUser ? (
          // Authenticated users get Layout wrapper
          <Route element={<Layout user={legacyUser} onLogout={handleLogout} />}>
            <Route path="/the-gallery" element={<TheCollectiv3 />} />
          </Route>
        ) : (
          // Non-authenticated users get direct access
          <Route path="/the-gallery" element={<TheCollectiv3 />} />
        )}

        {/* Routes WITH layout - for authenticated users */}
        <Route element={<Layout user={legacyUser} onLogout={handleLogout} />}>
          {/* Profile management */}
          <Route path="/update-profile" element={<UpdateProfile />} />

          {/* Main app routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <RoleBasedRoute>
                  <SceneManager user={legacyUser} onLogout={handleLogout} />
                </RoleBasedRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/scenes/:id/edit"
            element={
              <ProtectedRoute>
                <RoleBasedRoute>
                  <SceneManager user={legacyUser} onLogout={handleLogout} />
                </RoleBasedRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/gallery"
            element={
              <ProtectedRoute>
                <Gallery />
              </ProtectedRoute>
            }
          />

          {/* Legacy redirects */}
          <Route path="/library" element={<Navigate to="/the-gallery" replace />} />

          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <CustomerSupport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/feedback"
            element={
              <ProtectedRoute>
                <FeedbackForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          {enableLocalTeamEmail && <Route path="/team-email" element={<TeamEmailPage />} />}


          {/* Guide pages */}
          <Route path="/guides/indoor-capture" element={<IndoorCaptureGuidePage />} />
          <Route path="/guides/drone-capture" element={<DroneCaptureGuidePage />} />
        </Route>

        {/* Catch-all route */}
        <Route path="*" element={<Navigate to="/not-found" replace />} />
      </Routes>
      </RecoSessionProvider>
      </I18nProvider>
    </Router>
  );
}

export default App;
